import { useState, useContext } from 'react'
import { submitStudentFeedback } from '../feedbackApi'
import type { UseSubmitStudentFeedbackReturn, SubmitFeedbackRequest } from '../feedbackTypes'
import { BookingContext } from 'contexts/booking'

const useSubmitStudentFeedback = (): UseSubmitStudentFeedbackReturn => {
    const booking = useContext(BookingContext)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)

    const submitFeedback = async (feedback: SubmitFeedbackRequest): Promise<void> => {
        try {
            // Validate booking session status
            if (booking.status !== 'validated') {
                throw new Error('Session not validated. Cannot submit feedback.')
            }

            // Validate required context data
            if (!booking.classroomId) {
                throw new Error('Missing classroom ID')
            }
            if (!booking.user?.id) {
                throw new Error('Missing student ID')
            }
            if (!booking.auth) {
                throw new Error('Missing authentication token')
            }

            setLoading(true)
            setError(null)
            setSuccess(false)

            // Call the API function with context data
            const response = await submitStudentFeedback(
                booking.classroomId,
                booking.user.id,
                booking.auth,
                feedback
            )

            if (!response) {
                throw new Error('Failed to submit feedback')
            }

            if (response.success) {
                setSuccess(true)
            } else {
                throw new Error(response.message || 'Failed to submit feedback')
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to submit feedback'
            setError(errorMessage)
            console.error('Failed to submit student feedback:', err)
            throw err
        } finally {
            setLoading(false)
        }
    }

    return { submitFeedback, loading, error, success }
}

export { useSubmitStudentFeedback }
